// API client for PickleCourts backend
import type { VenueStatus } from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.picklecourts.rent';

// ============================================
// Types
// ============================================

interface ApiError {
  error: string;
}

export type AudienceLeaf = {
  field: string;
  operator: string;
  value?: unknown;
};

export type AudienceFilter = AudienceLeaf | {
  version?: number;
  all?: AudienceFilter[];
  any?: AudienceFilter[];
  not?: AudienceFilter;
};

export interface AudienceAttribute {
  attributePath: string;
  displayName: string;
  dataType: 'string' | 'enum' | 'number' | 'money' | 'boolean' | 'date' | 'timestamp' | 'string_array' | 'enum_array';
  category?: string;
  allowedOperators: string[];
  options?: string[];
  coverageCount?: number;
  coveragePercent?: number;
  filterEnabled?: boolean;
  sortEnabled?: boolean;
  unit?: 'USD' | null;
  storageUnit?: 'cents' | null;
  definitionVersion?: number;
  sourceSemantics?: string;
  sensitivity?: 'standard' | 'sensitive' | string;
}

export interface AudienceStatus {
  state: 'not_entitled' | 'connection_required' | 'connection_invalid' | 'initial_sync_required' | 'sync_queued' | 'syncing' | 'ready' | 'stale' | 'sync_failed';
  reason?: { code: string; message: string } | null;
  serverNow: string;
  venue: { id: string; name: string; timezone: string; isPublished?: boolean; city?: string; regionState?: string };
  entitlement?: { featureKey: 'audiences'; status: 'trial' | 'active' | 'suspended' | 'canceled'; startsAt: string; endsAt?: string | null } | null;
  connection: { state: 'missing' | 'pending' | 'valid' | 'invalid'; integrationId?: string | null; credentialStatus: string; accountEmailMasked?: string | null; validatedAt?: string | null; lastErrorCode?: string | null; lastErrorMessage?: string | null; reportOrganizationApproval?: 'required' | 'verified' };
  data: { sourceId?: string | null; freshness: 'none' | 'fresh' | 'stale' | 'expired'; lastSuccessfulAt?: string | null; lastSuccessfulRunId?: string | null; sourceCutoffAt?: string | null; nextScheduledAt?: string | null; memberCount: number; attributeCount: number; consecutiveFailures: number };
  activeRun?: AudienceSyncRun | null;
  latestRun?: AudienceSyncRun | null;
  manualRefresh: { allowed: boolean; reasonCode?: string | null; availableAt?: string | null; cooldownSeconds: number };
  capabilities: Record<'connect' | 'refresh' | 'search' | 'save' | 'interpret' | 'export' | 'publish', AudienceCapability>;
  warnings?: string[];
}

export interface AudienceCapability { allowed: boolean; reasonCode?: string | null; message?: string | null }
export interface AudienceSyncRun { id: string; trigger: 'initial' | 'manual' | 'scheduled'; status: 'queued' | 'running' | 'succeeded' | 'failed' | 'dead_lettered'; attemptCount: number; scheduledFor?: string | null; startedAt?: string | null; completedAt?: string | null; progress?: { phase?: string; rowsSeen?: number; rowsInserted?: number; rowsUpdated?: number; rowsArchived?: number }; lastErrorCode?: string | null; lastErrorMessage?: string | null }

export interface AudienceVisitor {
  id: string;
  displayName: string;
  email?: string | null;
  recordStatus?: string;
  summary?: Record<string, string | number | boolean | null | undefined>;
}

/** Venue-owned membership records. Billing terms are informational; no charges are created. */
export interface NativeMembershipType {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  billingCadence?: 'weekly' | 'monthly' | 'quarterly' | 'annual' | 'custom' | null;
  priceCents?: number | null;
  currency?: string | null;
}

export interface NativeMembershipAssignment {
  id: string;
  membershipTypeId: string;
  membershipTypeName?: string;
  startsAt: string;
  endsAt?: string | null;
  isActive: boolean;
  isCurrent?: boolean;
  billingCadence?: string | null;
  priceCents?: number | null;
  currency?: string | null;
  assignedAt?: string | null;
  deactivatedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface NativeMember {
  id: string;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  visitCount?: number;
  firstVisitedAt?: string | null;
  lastVisitedAt?: string | null;
  status: 'native_member' | 'external_guest';
  assignment?: NativeMembershipAssignment | null;
}

export interface MemberMembershipHistory extends NativeMembershipAssignment {}

export interface MemberDetail extends NativeMember {
  totalBookings?: number;
  completedVisitCount?: number;
  upcomingBookingCount?: number;
  assignmentHistory: MemberMembershipHistory[];
}

export interface NativeMembershipDirectory {
  members: NativeMember[];
  membershipTypes: NativeMembershipType[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
}

export interface AudienceDataSource {
  id: string;
  displayName: string;
  sourceType?: string;
  state: string;
  mode?: string;
  memberCount?: number;
  attributeCount?: number;
  lastSuccessfulAt?: string | null;
  sourceCutoff?: string | null;
  lastRun?: { status?: string; insertedCount?: number; updatedCount?: number; message?: string } | null;
}

export interface AudienceBootstrap {
  status: AudienceStatus;
  attributes: AudienceAttribute[];
  dataSources: AudienceDataSource[];
  audiences: SavedAudience[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  preview?: {
    // `members` is the backend contract key; the portal presents these records as visitors.
    members: AudienceVisitor[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    summary: { eligibleProfiles: number; matched: number; unknownCount?: number };
    filter: AudienceFilter;
    profileAsOf?: string | null;
    sourceUpdatedAt?: string | null;
  } | null;
}

export interface SavedAudience {
  id: string;
  name: string;
  description?: string | null;
  sourcePrompt?: string | null;
  filterDefinition: AudienceFilter;
  lastEstimatedCount?: number | null;
  lastEvaluatedAt?: string | null;
  updatedAt?: string | null;
}

export interface AudiencePublication {
  id: string;
  audienceId?: string | null;
  destination: 'courtreserve';
  groupName: string;
  status: 'queued' | 'processing' | 'succeeded' | 'succeeded_with_warnings' | 'failed' | 'dead_lettered';
  attempts: number;
  matchedMemberCount: number;
  eligibleEmailCount: number;
  addedCount: number;
  unmatchedCount: number;
  failedCount: number;
  externalGroupId?: string | null;
  externalGroupUrl?: string | null;
  lastErrorCode?: string | null;
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
  sourceRunId?: string | null;
  destinationId?: string | null;
  removedCount?: number;
  intendedMemberCount?: number;
  warningCount?: number;
  canRetry?: boolean;
}

export interface LoginResponse {
  token: string;
  admin: {
    id: string;
    email: string;
    venueId: string;
    role: 'SUPERADMIN' | 'VENUE_ADMIN';
  };
}

export interface Venue {
  id: string;
  name: string;
  slug: string;
  timezone?: string;
  region?: {
    id: string;
    name: string;
    slug: string;
    state: string;
  } | null;
  config: {
    description?: string;
    email?: string;
    phone?: string;
    website?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      zip: string;
    };
    photos?: Array<{ id: string; url: string; caption: string; isMain: boolean }>;
    operating_hours?: Array<{ dayOfWeek: number; open: string; close: string; isClosed: boolean }>;
    refund_policy?: {
      type: 'partial' | 'full' | 'none' | 'custom';
      fullRefundHours?: number;
      partialRefundHours?: number;
      partialRefundPercent?: number;
      customPolicy?: string;
    };
    waiver_pdf?: {
      enabled?: boolean;
      url?: string | null;
      file_name?: string | null;
      sha256?: string | null;
      uploaded_at?: string | null;
    };
    equipment_rental?: {
      enabled?: boolean;
      label?: string;
      price_cents?: number;
    };
    is_published?: boolean;
    booking_window_days?: number;
  };
  indoor_rate_cents: number;
  outdoor_rate_cents: number;
  payment_routing?: 'connect' | 'platform';
  created_at: string;
  updated_at: string;
}

export interface Court {
  id: string;
  venue_id: string;
  name: string;
  sport_type: string;
  court_type: 'indoor' | 'outdoor' | 'covered';
  mapped_external_name?: string | null;
  mapped_integration_type?: string | null;
  description?: string;
  amenities?: string[];
  photos?: Array<{ id: string; url: string; caption: string; isMain: boolean }>;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: string;
  court_id: string;
  customer_id: string;
  start_time: string;
  end_time: string;
  confirmation_code: string;
  status: 'checkout_pending' | 'pending_approval' | 'approved' | 'declined' | 'expired' | 'canceled' | 'completed'
    // legacy rows / cached responses:
    | 'manual_booking_approval' | 'pending' | 'confirmed';
  payment_flow?: string;
  source?: 'app' | 'manual' | 'phone' | 'walkin';
  notes?: string;
  created_at: string;
  updated_at: string;
  court_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  payment_amount_cents?: number;
  checkout_metadata?: {
    autoApproval?: {
      status?: string;
      courtreserveConfirmation?: string | null;
      externalRef?: { platform?: string; kind?: string; value?: string; verifiedAt?: string | null };
    };
    autoCancellation?: { status?: string };
    [key: string]: unknown;
  };
}

export interface Availability {
  id: string;
  court_id: string;
  day_of_week: number | null;
  specific_date: string | null;
  start_time: string;
  end_time: string;
  hourly_rate_cents: number;
  is_available: boolean;
}

export interface PricingRule {
  id: string;
  name: string;
  courtTypeFilter: string;
  // Exactly one of daysOfWeek (recurring) / specificDate (one-off, self-expiring) is set.
  daysOfWeek: number[] | null;
  specificDate?: string | null;
  startTime: string;
  endTime: string;
  rateCents: number;
  priority: number;
  isActive: boolean;
}

export interface DashboardResponse {
  venue: Venue;
  courts: Court[];
  stats: {
    totalCourts: number;
    totalBookings: number;
    upcomingBookings: number;
  };
}

export interface CreateCourtRequest {
  name: string;
  sportType: string;
  courtType: 'indoor' | 'outdoor' | 'covered';
  description?: string;
  amenities?: string[];
}

export interface BulkAvailabilityRequest {
  courtIds: string[];
  dayOfWeek: number[];
  startTime: string;
  endTime: string;
  hourlyRateCents: number;
}

export type PromoFundedBy = 'venue' | 'platform';

export interface PromoWindow {
  id?: string;
  dowMask: number;
  localStartTime: string;
  localEndTime: string;
}

export interface SuperadminPromo {
  id: string;
  code: string;
  venueId: string | null;
  venueName: string | null;
  percentOff: number;
  maxRedemptionsTotal: number | null;
  maxRedemptionsPerCustomer: number;
  maxRedemptionsPerDay: number | null;
  maxDiscountCentsPerRedemption: number | null;
  maxDiscountCentsTotal: number | null;
  totalDiscountCentsUsed: number;
  fundedBy: PromoFundedBy;
  active: boolean;
  startsAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  redemptionCount: number;
  consumedCount: number;
  availableRedemptionCount: number;
  platformAbsorbedCents: number;
  pendingVenueReimbursementCents: number;
  createdAt: string;
  windows: PromoWindow[];
}

export interface PlatformPromoSettings {
  dailyPlatformBudgetCents: number | null;
  dailyPlatformSpentCents: number;
  spendWindowStartedAt: string;
  manuallyPaused: boolean;
  pausedReason: string | null;
  pausedAt: string | null;
}

export interface PromoRedemptionLedgerRow {
  redemptionId: string;
  consumedAt: string;
  bookingId: string | null;
  customerEmail: string | null;
  promoCodeId: string;
  code: string;
  percentOff: number;
  codeFundedBy: PromoFundedBy;
  redemptionFundedBy: PromoFundedBy | null;
  venueId: string | null;
  venueName: string | null;
  hasConnectedAccount: boolean;
  discountCents: number | null;
  originalSubtotalCents: number | null;
  platformAbsorbedCents: number;
  pendingVenueReimbursementCents: number;
  settledAt: string | null;
  settlementAmountCents: number | null;
  stripeTransferId: string | null;
}

export interface ManualBookingApprovalRequest {
  id: string;
  confirmationCode: string;
  status: 'pending_approval' | 'approved' | 'declined';
  venueId: string;
  venueName: string;
  venueSlug: string;
  courtId: string;
  courtName: string;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
  startTime: string;
  endTime: string;
  paymentAmountCents: number;
  paymentStatus: string | null;
  paymentIntentId: string | null;
  totalFeeCents: number;
  serviceFeePercentCents: number;
  serviceFeeFlatCents: number;
  bookingFeeCents: number;
  subtotalAmountCents: number;
  // Mirrors backend MANUAL_QUEUE_STATUS wire values in backend/src/constants/checkout.js.
  queueStatus: 'pending' | 'claimed' | 'booked' | 'declined';
  claimedByAdminId: string | null;
  claimedAt: string | null;
  calledAt: string | null;
  venueConfirmationCode: string | null;
  internalBookingCostCents: number | null;
  actualVenueCostCents: number | null;
  capturedAmountCents: number | null;
  originalAuthAmountCents: number | null;
  releasedAmountCents: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface EarningsBooking {
  id: string;
  confirmation_code: string;
  start_time: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  court_name: string;
  amount_cents: number;
  platform_fee_amount: number | null;
  venue_amount: number | null;
  payment_status: string;
  payment_date: string;
  captured_at?: string | null;
  // Additive (backend ledger-truth fields; absent on older API builds)
  settlement_notes?: string | null;
  promo_credit?: boolean;
  // Itemized checkout pricing stamped at booking time (legacy rows: null)
  pricing_detail?: {
    baseAmountCents?: number;
    rentalAmountCents?: number;
    subtotalAmountCents?: number;
    serviceFeeAmountCents?: number;
    salesTaxBps?: number;
    salesTaxAmountCents?: number;
    totalAmountCents?: number;
  } | null;
  promo_detail?: {
    code?: string;
    percentOff?: number;
    discountCents?: number;
    originalAmountCents?: number;
    finalAmountCents?: number;
    fundedBy?: string;
  } | null;
}

export interface EarningsResponse {
  month: string;
  summary: {
    booking_count: number;
    gross_cents: number;
    platform_fee_cents: number;
    venue_amount_cents: number;
    refund_count?: number;
    refunded_cents?: number;
    sales_tax_cents?: number;
  };
  bookings: EarningsBooking[];
  venueNetCents?: number;
}

export interface VenueSettlementSummary {
  venueId: string;
  paymentRouting: 'connect' | 'platform';
  settlementCadence: 'weekly' | 'monthly';
  pending: {
    entryCount: number;
    grossCentsTotal: number;
    commissionCentsTotal: number;
    netToVenueCentsTotal: number;
    oldestEntryAt: string | null;
    newestEntryAt: string | null;
  };
  lastSettlement: { settlement_reference: string | null; settled_at: string | null } | null;
}

// ============================================
// Currency helpers
// ============================================

export function centsToDollars(cents: number): number {
  return cents / 100;
}

export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

export const MEDLEY_DEFAULT_VENUE_ID = '6d65646c-6579-4d61-726b-6574706c6163';

export function audienceBuilderBasePath(role: string | null, selectedVenueId: string | null): string {
  if (role === 'SUPERADMIN' && selectedVenueId) {
    return `/api/admin/superadmin/venues/${encodeURIComponent(selectedVenueId)}/audience-builder`;
  }
  return '/api/admin/audience-builder';
}

// ============================================
// API Client
// ============================================

export class ApiClient {
  private token: string | null = null;
  private selectedVenueId: string | null = null;
  private requestGeneration = 0;
  private inFlightGets = new Map<string, Promise<unknown>>();
  private audienceStatusCache: { generation: number; storedAt: number; value: AudienceStatus } | null = null;

  constructor() {
    this.token = localStorage.getItem('okc_auth_token');
    this.selectedVenueId = localStorage.getItem('okc_selected_venue_id');
  }

  setToken(token: string | null) {
    this.token = token;
    this.requestGeneration += 1;
    this.inFlightGets.clear();
    this.audienceStatusCache = null;
    if (token) {
      localStorage.setItem('okc_auth_token', token);
    } else {
      localStorage.removeItem('okc_auth_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  setSelectedVenueId(venueId: string | null) {
    this.selectedVenueId = venueId;
    this.requestGeneration += 1;
    this.inFlightGets.clear();
    this.audienceStatusCache = null;
    if (venueId) {
      localStorage.setItem('okc_selected_venue_id', venueId);
    } else {
      localStorage.removeItem('okc_selected_venue_id');
    }
  }

  getSelectedVenueId(): string | null {
    return this.selectedVenueId;
  }

  private getVenueQueryParam(): string {
    return this.selectedVenueId ? `?venueId=${this.selectedVenueId}` : '';
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const method = String(options.method || 'GET').toUpperCase();
    if (method !== 'GET') return this.performRequest<T>(endpoint, options);

    const key = `${this.requestGeneration}:${endpoint}`;
    const existing = this.inFlightGets.get(key);
    if (existing) return existing as Promise<T>;

    const pending = this.performRequest<T>(endpoint, options);
    this.inFlightGets.set(key, pending);
    try {
      return await pending;
    } finally {
      if (this.inFlightGets.get(key) === pending) this.inFlightGets.delete(key);
    }
  }

  private async performRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const nativeEndpoint = endpoint
      .replace(/^\/api\/admin(?=\/|$)/, '/api/native/admin')
      .replace(/^\/api\/stripe\/connect(?=\/|$)/, '/api/native/admin/stripe/connect');
    const url = `${API_BASE_URL}${nativeEndpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, { ...options, headers });
    const raw = await response.text();
    const data = raw ? JSON.parse(raw) : null;

    if (!response.ok) {
      if (response.status === 401 && this.token) {
        // Token expired or invalid — clear auth and redirect to login
        this.token = null;
        localStorage.removeItem('okc_auth_token');
        localStorage.removeItem('okc_business_user');
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }
      if (response.status === 304) {
        throw new Error('Request returned no fresh data');
      }
      throw new Error((data as ApiError | null)?.error || 'Request failed');
    }

    return (data ?? ({} as T)) as T;
  }

  // ============================================
  // Auth
  // ============================================

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(response.token);
    return response;
  }

  logout() {
    this.setToken(null);
    this.setSelectedVenueId(null);
  }

  async requestAdminPasswordReset(email: string): Promise<{ success: boolean }> {
    return this.request('/api/auth/admin/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetAdminPassword(token: string, newPassword: string): Promise<{ success: boolean }> {
    return this.request('/api/auth/admin/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // ============================================
  // Venue
  // ============================================

  async getVenue(): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/venue${this.getVenueQueryParam()}`);
  }

  async updateVenue(data: { name?: string; config?: Venue['config'] }): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/venue${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateVenuePricing(data: { indoorRateCents?: number; outdoorRateCents?: number }): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/venue/pricing${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // Stripe Connect / Payments
  // ============================================

  async getStripeConnectStatus(): Promise<{
    venueId: string;
    connectedAccountId: string | null;
    onboardingStatus: string | null;
    detailsSubmitted: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    requirements: Record<string, any> | null;
    feeBps: number | null;
  }> {
    return this.request(`/api/stripe/connect/status${this.getVenueQueryParam()}`);
  }

  async createStripeConnectAccount(): Promise<{ connectedAccountId: string; onboardingStatus: string }> {
    return this.request(`/api/stripe/connect/account${this.getVenueQueryParam()}`, { method: 'POST' });
  }

  async getStripeOnboardingLink(returnUrl: string, refreshUrl: string): Promise<{ url: string }> {
    return this.request(`/api/stripe/connect/onboarding-link${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify({ returnUrl, refreshUrl }),
    });
  }

  async getPaymentsEarnings(month?: string): Promise<EarningsResponse> {
    const params = new URLSearchParams();
    if (this.selectedVenueId) params.set('venueId', this.selectedVenueId);
    if (month) params.set('month', month);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/admin/payments/earnings${q}`);
  }

  // ============================================
  // Courts
  // ============================================

  async getCourts(): Promise<Court[]> {
    return this.request(`/api/admin/courts${this.getVenueQueryParam()}`);
  }

  async createCourt(data: CreateCourtRequest): Promise<Court> {
    return this.request(`/api/admin/courts${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCourt(id: string, data: Partial<CreateCourtRequest & { isActive?: boolean }>): Promise<Court> {
    return this.request(`/api/admin/courts/${id}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCourt(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/courts/${id}${this.getVenueQueryParam()}`, { method: 'DELETE' });
  }

  // ============================================
  // Bookings
  // ============================================

  async getBookings(): Promise<Booking[]> {
    return this.request(`/api/admin/bookings${this.getVenueQueryParam()}`);
  }

  async updateBooking(id: string, data: {
    status?: Booking['status'];
    notes?: string;
    courtId?: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    source?: string;
    customer?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
  }): Promise<Booking> {
    return this.request(`/api/admin/bookings/${id}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async approveReservation(id: string): Promise<{ reservation: { id: string; status: string }; payment: { status: string } }> {
    return this.request(`/api/reservations/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async declineReservation(id: string): Promise<{ reservation: { id: string; status: string }; payment: { status: string } }> {
    return this.request(`/api/reservations/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // ============================================
  // Availability
  // ============================================

  async getAvailabilitySummary(courtId: string): Promise<{ courtId: string; slots: Availability[] }> {
    return this.request(`/api/admin/courts/${courtId}/availability/summary${this.getVenueQueryParam()}`);
  }

  async createAvailability(
    courtId: string,
    data: {
      dayOfWeek?: number;
      specificDate?: string;
      startTime: string;
      endTime: string;
      hourlyRateCents: number;
      isAvailable?: boolean;
    }
  ): Promise<{ created: number; slots: Availability[] }> {
    return this.request(`/api/admin/courts/${courtId}/availability${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAvailability(
    courtId: string,
    options?: { dayOfWeek?: number; specificDate?: string; all?: boolean }
  ): Promise<{ deleted: number }> {
    const params = new URLSearchParams();
    if (this.selectedVenueId) params.set('venueId', this.selectedVenueId);
    if (options?.all) params.set('all', 'true');
    if (options?.dayOfWeek !== undefined) params.set('dayOfWeek', String(options.dayOfWeek));
    if (options?.specificDate) params.set('specificDate', options.specificDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/admin/courts/${courtId}/availability${query}`, { method: 'DELETE' });
  }

  async createBulkAvailability(data: BulkAvailabilityRequest): Promise<{ results: Array<{ courtId: string; created: number }> }> {
    return this.request(`/api/admin/courts/availability/bulk${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAvailabilitySlot(
    slotId: string,
    data: { hourlyRateCents?: number; isAvailable?: boolean }
  ): Promise<{ slot: Availability }> {
    return this.request(`/api/admin/availability/${slotId}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdateAvailabilitySlots(
    slotIds: string[],
    data: { hourlyRateCents?: number; isAvailable?: boolean }
  ): Promise<{ updated: number }> {
    return this.request(`/api/admin/availability/bulk-update${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify({ slotIds, ...data }),
    });
  }

  async bulkCreateAvailabilitySlots(
    courtId: string,
    slots: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      hourlyRateCents: number;
      isAvailable: boolean;
    }>
  ): Promise<{ created: number }> {
    return this.request(`/api/admin/courts/${courtId}/availability/bulk-create${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify({ slots }),
    });
  }

  async getAvailabilityForDate(date: string): Promise<{
    date: string;
    slotsByCourtId: Record<string, Array<{
      start_time: string;
      end_time: string;
      hourly_rate_cents: number;
      status: 'available' | 'unavailable' | 'booked';
      isOverride?: boolean;
      booking?: {
        id: string;
        confirmationCode: string;
        customerName: string;
        status: string;
        notes?: string;
      };
    }>>;
    integration?: {
      integrationId: string;
      integrationType: string;
      lastSyncAt: string | null;
      syncStatus: string;
    } | null;
  }> {
    const params = new URLSearchParams();
    if (this.selectedVenueId) params.set('venueId', this.selectedVenueId);
    params.set('date', date);
    return this.request(`/api/admin/availability/date?${params.toString()}`);
  }

  // ============================================
  // Dashboard
  // ============================================

  async getDashboard(): Promise<DashboardResponse> {
    return this.request('/api/admin/dashboard');
  }

  // ============================================
  // Photos
  // ============================================

  async getPhotoUploadUrl(
    filename: string,
    contentType: string,
    folder?: 'venue' | 'courts' | 'waivers'
  ): Promise<{ uploadUrl: string; publicUrl: string; key: string }> {
    return this.request('/api/admin/photos/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, folder }),
    });
  }

  async uploadPhotoToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo to S3');
    }
  }

  async deletePhoto(url: string): Promise<{ success: boolean }> {
    return this.request('/api/admin/photos', {
      method: 'DELETE',
      body: JSON.stringify({ url }),
    });
  }

  // ============================================
  // Superadmin
  // ============================================

  async getSuperadminVenues(params?: { search?: string; page?: number; limit?: number; paymentRouting?: 'connect' | 'platform' }): Promise<{
    venues: Array<{
      id: string;
      name: string;
      slug: string;
      regionId: string | null;
      city: string;
      state: string;
      isPublished: boolean;
      venueMode: VenueStatus;
      paymentRouting: 'connect' | 'platform';
      mercuryRecipientId: string | null;
      availabilityStatusOverride: 'available' | 'unavailable' | 'unknown' | null;
      stripeStatus: 'pending' | 'restricted' | 'completed' | null;
      stripeChargesEnabled: boolean;
      stripePayoutsEnabled: boolean;
      createdAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.paymentRouting) queryParams.set('paymentRouting', params.paymentRouting);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.request(`/api/admin/superadmin/venues${query}`);
  }

  // Full venue listing (id/name/location only) in one request. Use this for
  // dropdowns instead of paging getSuperadminVenues — sequential page loops
  // trip Cloudflare's per-IP rate limit now that the catalog is large.
  async getSuperadminVenueOptions(): Promise<{
    venues: Array<{ id: string; name: string; city: string; state: string }>;
  }> {
    return this.request('/api/admin/superadmin/venues/options');
  }

  async getManualBookingApprovalRequests(): Promise<{ requests: ManualBookingApprovalRequest[] }> {
    return this.request('/api/admin/superadmin/manual-booking-requests');
  }

  async claimManualBookingApprovalRequest(id: string): Promise<{ request: ManualBookingApprovalRequest }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/claim`, {
      method: 'POST',
    });
  }

  async completeManualBookingApprovalRequest(
    id: string,
    data: { actualVenueCostCents: number; venueConfirmationCode?: string; internalBookingCostCents?: number | null }
  ): Promise<{ request: ManualBookingApprovalRequest }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async declineManualBookingApprovalRequest(
    id: string,
    data: { reason?: string }
  ): Promise<{ request: ManualBookingApprovalRequest }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getRegions(): Promise<Array<{ id: string; name: string; slug: string; state: string }>> {
    return this.request('/api/regions');
  }

  async setSuperadminVenueRegion(venueId: string, regionId: string): Promise<{ venue: Venue }> {
    const params = new URLSearchParams();
    params.set('venueId', venueId);

    return this.request(`/api/admin/venue?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({
        regionId,
      }),
    });
  }

  async setSuperadminVenueMode(venueId: string, venueMode: VenueStatus): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/mode`, {
      method: 'PUT',
      body: JSON.stringify({ venueMode }),
    });
  }

  async setSuperadminVenuePublished(venueId: string, published: boolean): Promise<{ venue: Venue }> {
    const params = new URLSearchParams();
    params.set('venueId', venueId);

    return this.request(`/api/admin/venue?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({
        config: {
          is_published: published,
        },
      }),
    });
  }

  async setSuperadminVenueAvailabilityOverride(
    venueId: string,
    value: 'available' | 'unavailable' | 'unknown' | null
  ): Promise<{ venue: Venue }> {
    const params = new URLSearchParams();
    params.set('venueId', venueId);

    return this.request(`/api/admin/venue?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({
        config: {
          map_availability_status_override: value,
        },
      }),
    });
  }

  async switchVenue(venueId: string): Promise<{ token: string; venue: { id: string; name: string; slug: string } }> {
    const response = await this.request<{ token: string; venue: { id: string; name: string; slug: string } }>(
      '/api/admin/superadmin/switch-venue',
      {
        method: 'POST',
        body: JSON.stringify({ venueId }),
      }
    );
    // Update the token
    this.setToken(response.token);
    return response;
  }

  // ============================================
  // Team/Admin Management
  // ============================================

  async getTeamMembers(): Promise<{ admins: Array<{
    id: string;
    email: string;
    role: string;
    created_at: string;
    invited_at?: string;
    accepted_at?: string;
    invited_by_email?: string;
  }> }> {
    return this.request(`/api/admin/team${this.getVenueQueryParam()}`);
  }

  async createTeamMember(data: {
    email: string;
    role?: 'VENUE_ADMIN' | 'VENUE_STAFF';
  }): Promise<{ admin: { id: string; email: string; role: string } }> {
    return this.request(`/api/admin/team${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeTeamMember(adminId: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/team/${adminId}${this.getVenueQueryParam()}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // Native memberships (venue-managed, no billing)
  // ============================================

  async getNativeMembershipDirectory(options: { page?: number; limit?: number; search?: string } = {}): Promise<NativeMembershipDirectory> {
    const params = new URLSearchParams({ page: String(options.page || 1), limit: String(options.limit || 50) });
    if (options.search?.trim()) params.set('search', options.search.trim());
    const [typeResponse, guestResponse] = await Promise.all([
      this.request<{ membershipTypes: NativeMembershipType[] }>('/api/admin/members/membership-types'),
      this.request<{ guests: Array<{
        customerId: string; firstName?: string | null; lastName?: string | null; email?: string | null; phone?: string | null;
        bookingCount?: number; firstVisitedAt?: string | null; lastVisitedAt?: string | null; status: 'native_member' | 'external_guest';
        membership?: { id: string; membershipTypeId: string; membershipTypeName?: string; startsOn: string; endsOn?: string | null; isActive: boolean; isCurrent?: boolean; billingCadence?: string | null; priceCents?: number | null; currency?: string | null; assignedAt?: string | null; deactivatedAt?: string | null; createdAt?: string | null; updatedAt?: string | null } | null;
      }>; pagination: NativeMembershipDirectory['pagination'] }>(`/api/admin/members/guests?${params.toString()}`),
    ]);
    return {
      membershipTypes: typeResponse.membershipTypes || [],
      members: (guestResponse.guests || []).map(guest => ({
        id: guest.customerId,
        displayName: [guest.firstName, guest.lastName].filter(Boolean).join(' ').trim() || guest.email || 'Guest',
        email: guest.email,
        phone: guest.phone,
        visitCount: guest.bookingCount || 0,
        firstVisitedAt: guest.firstVisitedAt,
        lastVisitedAt: guest.lastVisitedAt,
        status: guest.status,
        assignment: guest.membership ? {
          id: guest.membership.id,
          membershipTypeId: guest.membership.membershipTypeId,
          membershipTypeName: guest.membership.membershipTypeName,
          startsAt: guest.membership.startsOn,
          endsAt: guest.membership.endsOn,
          isActive: guest.membership.isActive,
          isCurrent: guest.membership.isCurrent,
          billingCadence: guest.membership.billingCadence,
          priceCents: guest.membership.priceCents,
          currency: guest.membership.currency,
          assignedAt: guest.membership.assignedAt,
          deactivatedAt: guest.membership.deactivatedAt,
          createdAt: guest.membership.createdAt,
          updatedAt: guest.membership.updatedAt,
        } : null,
      })),
      pagination: guestResponse.pagination,
    };
  }

  async getNativeMemberDetail(customerId: string): Promise<{ member: MemberDetail }> {
    const response = await this.request<{ member: {
      customerId: string; firstName?: string | null; lastName?: string | null; email?: string | null; phone?: string | null;
      bookingCount?: number; totalBookings?: number; completedVisitCount?: number; upcomingBookingCount?: number; firstVisitedAt?: string | null; lastVisitedAt?: string | null; status: 'native_member' | 'external_guest';
      assignment?: { id: string; membershipTypeId: string; membershipTypeName?: string; startsOn: string; endsOn?: string | null; isActive: boolean; isCurrent?: boolean; billingCadence?: string | null; priceCents?: number | null; currency?: string | null; assignedAt?: string | null; deactivatedAt?: string | null; createdAt?: string | null; updatedAt?: string | null } | null;
      assignmentHistory?: Array<{ id: string; membershipTypeId: string; membershipTypeName?: string; startsOn: string; endsOn?: string | null; isActive: boolean; isCurrent?: boolean; billingCadence?: string | null; priceCents?: number | null; currency?: string | null; assignedAt?: string | null; deactivatedAt?: string | null; createdAt?: string | null; updatedAt?: string | null }>;
    } }>(`/api/admin/members/guests/${encodeURIComponent(customerId)}`);
    const member = response.member;
    const mapAssignment = (assignment: NonNullable<typeof member.assignment | NonNullable<typeof member.assignmentHistory>[number]>): MemberMembershipHistory => ({
      id: assignment.id,
      membershipTypeId: assignment.membershipTypeId,
      membershipTypeName: assignment.membershipTypeName,
      startsAt: assignment.startsOn,
      endsAt: assignment.endsOn,
      isActive: assignment.isActive,
      isCurrent: assignment.isCurrent,
      billingCadence: 'billingCadence' in assignment ? assignment.billingCadence : null,
      priceCents: 'priceCents' in assignment ? assignment.priceCents : null,
      currency: 'currency' in assignment ? assignment.currency : null,
      assignedAt: 'assignedAt' in assignment ? assignment.assignedAt : null,
      deactivatedAt: 'deactivatedAt' in assignment ? assignment.deactivatedAt : null,
      createdAt: 'createdAt' in assignment ? assignment.createdAt : null,
      updatedAt: 'updatedAt' in assignment ? assignment.updatedAt : null,
    });
    return {
      member: {
        id: member.customerId,
        displayName: [member.firstName, member.lastName].filter(Boolean).join(' ').trim() || member.email || 'Guest',
        email: member.email,
        phone: member.phone,
        visitCount: member.bookingCount || 0,
        totalBookings: member.totalBookings ?? member.bookingCount ?? 0,
        completedVisitCount: member.completedVisitCount,
        upcomingBookingCount: member.upcomingBookingCount,
        firstVisitedAt: member.firstVisitedAt,
        lastVisitedAt: member.lastVisitedAt,
        status: member.status,
        assignment: member.assignment ? mapAssignment(member.assignment) : null,
        assignmentHistory: (member.assignmentHistory || []).map(mapAssignment),
      },
    };
  }

  async createNativeMembershipType(data: { name: string; description?: string | null; isActive?: boolean; billingCadence?: NativeMembershipType['billingCadence']; priceCents?: number | null; currency?: string | null }): Promise<{ membershipType: NativeMembershipType }> {
    return this.request('/api/admin/members/membership-types', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateNativeMembershipType(id: string, data: { name?: string; description?: string | null; isActive?: boolean; billingCadence?: NativeMembershipType['billingCadence']; priceCents?: number | null; currency?: string | null }): Promise<{ membershipType: NativeMembershipType }> {
    return this.request(`/api/admin/members/membership-types/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async assignNativeMembership(data: { memberId: string; membershipTypeId: string; startsAt: string; endsAt?: string | null; isActive?: boolean }): Promise<{ assignment: NativeMembershipAssignment }> {
    return this.request('/api/admin/members/memberships', { method: 'POST', body: JSON.stringify({ customerId: data.memberId, membershipTypeId: data.membershipTypeId, startsOn: data.startsAt, endsOn: data.endsAt, isActive: data.isActive }) });
  }

  async updateNativeMembershipAssignment(id: string, data: { membershipTypeId?: string; startsAt?: string; endsAt?: string | null; isActive?: boolean }): Promise<{ assignment: NativeMembershipAssignment }> {
    return this.request(`/api/admin/members/memberships/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ membershipTypeId: data.membershipTypeId, startsOn: data.startsAt, endsOn: data.endsAt, isActive: data.isActive }) });
  }

  async removeNativeMembershipAssignment(id: string): Promise<{ success: boolean }> {
    await this.updateNativeMembershipAssignment(id, { isActive: false });
    return { success: true };
  }

  // ============================================
  // Integrations / Sync
  // ============================================

  async getIntegrations(): Promise<{ integrations: Array<{
    id: string;
    venue_id: string;
    integration_type: string;
    external_id: string;
    config: any;
    status: string;
    sync_status: string;
    auto_sync_enabled: boolean;
    sync_interval_minutes: number;
    sync_active_start_hour: number | null;
    sync_active_end_hour: number | null;
    last_sync_at: string | null;
    last_error: string | null;
  }> }> {
    return this.request(`/api/integrations${this.getVenueQueryParam()}`);
  }

  async triggerSync(integrationId: string): Promise<{ queued: boolean; sync_status: string }> {
    return this.request(`/api/integrations/${integrationId}/sync${this.getVenueQueryParam()}`, {
      method: 'POST',
    });
  }

  async getSyncStatus(integrationId: string): Promise<{
    sync_status: string;
    last_sync_at: string | null;
    last_error: string | null;
  }> {
    return this.request(`/api/integrations/${integrationId}/sync-status${this.getVenueQueryParam()}`);
  }

  async updateAutoSync(integrationId: string, data: {
    enabled: boolean;
    intervalMinutes?: 5 | 15 | 30 | 60;
    activeStartHour?: number | null;
    activeEndHour?: number | null;
  }): Promise<{ integration: any }> {
    return this.request(`/api/integrations/${integrationId}/auto-sync${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteIntegration(integrationId: string): Promise<{ success: boolean }> {
    return this.request(`/api/integrations/${integrationId}${this.getVenueQueryParam()}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // Pricing Rules
  // ============================================

  async getPricingRules(): Promise<{ rules: PricingRule[] }> {
    return this.request(`/api/admin/pricing-rules${this.getVenueQueryParam()}`);
  }

  async createPricingRule(data: Omit<PricingRule, 'id'>): Promise<{ rule: PricingRule }> {
    return this.request(`/api/admin/pricing-rules${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePricingRule(id: string, data: Partial<PricingRule>): Promise<{ rule: PricingRule }> {
    return this.request(`/api/admin/pricing-rules/${id}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePricingRule(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/pricing-rules/${id}${this.getVenueQueryParam()}`, {
      method: 'DELETE',
    });
  }

  // Business Tiers & MSA
  // ============================================

  async getBusinessTiers(): Promise<{ tiers: Array<{ id: number; name: string; display_name: string; color: string; sort_order: number }> }> {
    return this.request('/api/admin/billing-tiers');
  }

  async setVenueTier(venueId: string, tierId: number | null): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tierId }),
    });
  }

  async setVenueCommission(venueId: string, commissionBps: number): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/commission`, {
      method: 'PUT',
      body: JSON.stringify({ commissionBps }),
    });
  }

  async setVenueContact(venueId: string, data: {
    legalName?: string;
    contactName?: string;
    contactTitle?: string;
    contactEmail?: string;
  }): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/contact`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async generateVenueDocs(venueId: string, opts?: { contactName?: string; contactEmail?: string; legalName?: string }): Promise<{ packet: { id: number; status: string; effectiveDate: string } }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/generate-docs`, {
      method: 'POST',
      body: opts ? JSON.stringify(opts) : undefined,
    });
  }

  async getMsaCurrent(): Promise<{
    packet: {
      id: number;
      status: string;
      commissionRateBps: number;
      effectiveDate: string;
      signedAt: string | null;
      signerName: string | null;
      docs: {
        masterAgreement: string;
        dataPrivacy: string;
        bookingOperations: string;
        marketingConsent: string;
        signaturePage: string;
      };
    } | null;
  }> {
    return this.request(`/api/admin/msa/current${this.getVenueQueryParam()}`);
  }

  async signMsa(packetId: number, signerName: string, signerEmail: string): Promise<{ success: boolean; signedAt: string }> {
    return this.request(`/api/admin/msa/sign${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify({ packetId, signerName, signerEmail }),
    });
  }

  // ============================================
  // Tenant admins remain scoped exclusively by their authenticated token.
  // Superadmins use the explicit support route for the venue selected in the
  // portal. The API still authorizes the role and venue on every request.
  // ============================================

  private audienceBuilderPath(path: string): string {
    let role: string | null = null;
    try {
      role = JSON.parse(localStorage.getItem('okc_business_user') || 'null')?.role || null;
    } catch {
      role = null;
    }

    return `${audienceBuilderBasePath(role, this.selectedVenueId)}${path}`;
  }

  private rememberAudienceStatus(value: AudienceStatus): AudienceStatus {
    this.audienceStatusCache = {
      generation: this.requestGeneration,
      storedAt: Date.now(),
      value,
    };
    return value;
  }

  async getAudienceBuilderStatus(options: { maxAgeMs?: number } = {}): Promise<AudienceStatus> {
    const maxAgeMs = Math.max(0, Number(options.maxAgeMs) || 0);
    const cached = this.audienceStatusCache;
    if (maxAgeMs > 0 && cached
      && cached.generation === this.requestGeneration
      && Date.now() - cached.storedAt < maxAgeMs) {
      return cached.value;
    }
    return this.rememberAudienceStatus(await this.request(this.audienceBuilderPath('/status')));
  }

  async getAudienceBootstrap(): Promise<AudienceBootstrap> {
    const result = await this.request<AudienceBootstrap>(this.audienceBuilderPath('/bootstrap'));
    this.rememberAudienceStatus(result.status);
    return result;
  }

  async saveAudienceConnection(data: { courtReserveUrl: string; adminEmail: string; adminPassword: string }, idempotencyKey?: string): Promise<{ connection: AudienceStatus['connection']; run?: AudienceSyncRun; reused?: boolean }> {
    return this.request(this.audienceBuilderPath('/connection'), { method: 'PUT', headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined, body: JSON.stringify(data) });
  }

  async validateAudienceConnection(): Promise<{ connection: AudienceStatus['connection'] }> {
    return this.request(this.audienceBuilderPath('/connection/validate'), { method: 'POST', body: '{}' });
  }

  async revokeAudienceConnection(): Promise<void> {
    await this.request(this.audienceBuilderPath('/connection'), { method: 'DELETE' });
  }

  async requestAudienceSync(idempotencyKey?: string): Promise<{ run: AudienceSyncRun; reused: boolean }> {
    return this.request(this.audienceBuilderPath('/sync-runs'), { method: 'POST', headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined, body: JSON.stringify({ trigger: 'manual' }) });
  }

  async getAudienceSyncRuns(limit = 20): Promise<{ runs: AudienceSyncRun[]; nextCursor?: string | null }> {
    return this.request(`${this.audienceBuilderPath('/sync-runs')}?limit=${limit}`);
  }

  async getAudienceAttributes(): Promise<{ attributes: AudienceAttribute[] }> {
    return this.request(this.audienceBuilderPath('/attributes'));
  }

  async getAudienceDataSources(): Promise<{ dataSources: AudienceDataSource[] }> {
    return this.request(this.audienceBuilderPath('/data-sources'));
  }

  async searchAudienceVisitors(data: { query?: string; filter: AudienceFilter; page?: number; limit?: number }, options: { signal?: AbortSignal } = {}): Promise<{
    // Preserve the existing backend response key while using visitor terminology in portal code.
    members: AudienceVisitor[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    summary: { eligibleProfiles: number; matched: number; unknownCount?: number };
    filter: AudienceFilter;
    profileAsOf?: string | null;
    sourceUpdatedAt?: string | null;
  }> {
    return this.request(this.audienceBuilderPath('/members/search'), {
      method: 'POST', body: JSON.stringify(data), signal: options.signal,
    });
  }

  async getAudienceEmails(data: { query?: string; filter: AudienceFilter }): Promise<{ emails: string[]; count: number; truncated?: boolean }> {
    return this.request(this.audienceBuilderPath('/members/emails'), {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async getSavedAudiences(page = 1, limit = 20): Promise<{ audiences: SavedAudience[]; pagination?: { page: number; totalPages: number; total: number } }> {
    const base = this.audienceBuilderPath('/audiences');
    return this.request(`${base}${base.includes('?') ? '&' : '?'}page=${page}&limit=${limit}`);
  }

  async saveAudience(data: { name: string; description?: string; sourcePrompt?: string | null; filter: AudienceFilter }): Promise<{ audience: SavedAudience }> {
    return this.request(this.audienceBuilderPath('/audiences'), { method: 'POST', body: JSON.stringify(data) });
  }

  async updateSavedAudience(id: string, data: { name: string; description?: string; sourcePrompt?: string | null; filter: AudienceFilter; updatedAt?: string | null }): Promise<{ audience: SavedAudience }> {
    return this.request(this.audienceBuilderPath(`/audiences/${id}`), { method: 'PUT', headers: data.updatedAt ? { 'If-Match': `"${data.updatedAt}"` } : undefined, body: JSON.stringify(data) });
  }

  async previewSavedAudience(id: string): Promise<{ count: number; audience?: SavedAudience }> {
    return this.request(this.audienceBuilderPath(`/audiences/${id}/preview`), { method: 'POST', body: JSON.stringify({}) });
  }

  async deleteSavedAudience(id: string): Promise<void> {
    await this.request(this.audienceBuilderPath(`/audiences/${id}`), { method: 'DELETE' });
  }

  async interpretAudienceFilter(text: string, currentFilter: AudienceFilter): Promise<{
    filter: AudienceFilter;
    interpretation: string[];
    warnings?: string[];
    provider?: string;
  }> {
    return this.request(this.audienceBuilderPath('/filters/interpret'), {
      method: 'POST', body: JSON.stringify({ text, currentFilter }),
    });
  }

  async publishAudienceToCourtReserve(audienceId: string, data: { groupName?: string; idempotencyKey: string }): Promise<{ publication: AudiencePublication }> {
    return this.request(this.audienceBuilderPath(`/audiences/${audienceId}/publications`), {
      method: 'POST', headers: { 'Idempotency-Key': data.idempotencyKey }, body: JSON.stringify({ destination: 'courtreserve', groupName: data.groupName }),
    });
  }

  async getAudiencePublication(id: string): Promise<{ publication: AudiencePublication }> {
    return this.request(this.audienceBuilderPath(`/publications/${id}`));
  }

  async getAudiencePublications(audienceId: string): Promise<{ publications: AudiencePublication[]; nextCursor?: string | null }> {
    return this.request(this.audienceBuilderPath(`/audiences/${audienceId}/publications`));
  }

  async retryAudiencePublication(publicationId: string, idempotencyKey: string): Promise<{ publication: AudiencePublication; reused?: boolean }> {
    return this.request(this.audienceBuilderPath(`/publications/${publicationId}/retry`), { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: '{}' });
  }

  // ============================================
  // Health
  // ============================================

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }

  async getSuperadminPromos(venueId?: string | null): Promise<{ promos: SuperadminPromo[] }> {
    const query = venueId ? `?venueId=${encodeURIComponent(venueId)}` : '';
    return this.request(`/api/admin/superadmin/promos${query}`);
  }

  async createSuperadminPromo(data: {
    code?: string;
    venueId?: string | null;
    percentOff: number;
    fundedBy?: PromoFundedBy;
    maxDiscountCentsPerRedemption?: number | null;
    maxDiscountCentsTotal?: number | null;
    maxRedemptionsTotal?: number | null;
    maxRedemptionsPerCustomer?: number;
    maxRedemptionsPerDay?: number | null;
    startsAt?: string | null;
    expiresAt?: string | null;
    notes?: string | null;
    windows?: PromoWindow[];
  }): Promise<{ promo: Partial<SuperadminPromo> }> {
    return this.request('/api/admin/superadmin/promos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSuperadminPromoSettings(): Promise<{ settings: PlatformPromoSettings }> {
    return this.request('/api/admin/superadmin/promos/settings');
  }

  async updateSuperadminPromoSettings(data: {
    dailyPlatformBudgetCents?: number | null;
    manuallyPaused?: boolean;
    pausedReason?: string | null;
  }): Promise<{ settings: PlatformPromoSettings }> {
    return this.request('/api/admin/superadmin/promos/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async listSuperadminPromoRedemptions(opts: { onlyOutstanding?: boolean } = {}): Promise<{ redemptions: PromoRedemptionLedgerRow[] }> {
    const q = opts.onlyOutstanding ? '?onlyOutstanding=true' : '';
    return this.request(`/api/admin/superadmin/promos/redemptions${q}`);
  }

  async settleSuperadminPromoRedemption(redemptionId: string): Promise<{
    alreadySettled: boolean;
    transferId: string | null;
    redemption: { id: string; settledAt: string | null; settlementAmountCents: number | null; stripeTransferId: string | null };
  }> {
    return this.request(`/api/admin/superadmin/promos/redemptions/${redemptionId}/settle`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async setSuperadminPromoActive(promoId: string, active: boolean): Promise<{ promo: { id: string; active: boolean } }> {
    return this.request(`/api/admin/superadmin/promos/${promoId}`, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    });
  }

  async updateSuperadminPromoWindows(promoId: string, windows: PromoWindow[]): Promise<{ promo: { id: string; windows: PromoWindow[] } }> {
    return this.request(`/api/admin/superadmin/promos/${promoId}`, {
      method: 'PUT',
      body: JSON.stringify({ windows }),
    });
  }

  // --- Settlement (platform-collect partner monthly ledger) ---

  async getVenueSettlementSummary(venueId: string): Promise<VenueSettlementSummary> {
    return this.request(`/api/admin/venues/${venueId}/settlement-summary`);
  }

  async listSettlementEntries(
    venueId: string,
    opts: { status?: 'pending' | 'settled' | 'voided'; limit?: number } = {}
  ): Promise<{ entries: Array<SettlementEntry> }> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.limit) params.set('limit', String(opts.limit));
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-entries${q}`);
  }

  async markSettlementEntriesSettled(
    venueId: string,
    data: { entryIds: string[]; reference: string; notes?: string }
  ): Promise<{ updated: SettlementEntry[]; skipped?: string[]; message?: string }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-entries/mark-settled`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async voidSettlementEntry(entryId: string, notes: string): Promise<{ entry: SettlementEntry }> {
    return this.request(`/api/admin/superadmin/settlement-entries/${entryId}/void`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async recordSettlementManualAdjustment(
    venueId: string,
    data: { paymentId: string; bookingId?: string; grossCents: number; commissionBps?: number; currency?: string; notes: string }
  ): Promise<{ entry: SettlementEntry }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-entries/manual-adjustment`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async setSuperadminVenuePaymentRouting(
    venueId: string,
    paymentRouting: 'connect' | 'platform'
  ): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/payment-routing`, {
      method: 'PUT',
      body: JSON.stringify({ paymentRouting }),
    });
  }

  async setSuperadminVenueMercuryRecipient(
    venueId: string,
    mercuryRecipientId: string | null
  ): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/mercury-recipient`, {
      method: 'PUT',
      body: JSON.stringify({ mercuryRecipientId }),
    });
  }

  // ---- Settlement statements (monthly rollups) ----

  async listSettlementStatements(venueId: string): Promise<{ statements: SettlementStatement[] }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-statements`);
  }

  // Venue-facing (non-superadmin) statement history: finalized + paid only.
  async getVenueSettlementStatements(venueId: string): Promise<{ statements: SettlementStatement[] }> {
    return this.request(`/api/admin/venues/${venueId}/settlement-statements`);
  }

  async generateSettlementStatement(
    venueId: string,
    periodStart: string
  ): Promise<{ statement: SettlementStatement; note: string | null }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-statements`, {
      method: 'POST',
      body: JSON.stringify({ periodStart }),
    });
  }

  async finalizeSettlementStatement(statementId: string): Promise<{ statement: SettlementStatement }> {
    return this.request(`/api/admin/superadmin/settlement-statements/${statementId}/finalize`, { method: 'POST' });
  }

  async markSettlementStatementPaid(
    statementId: string,
    data: { paymentReference: string; notes?: string }
  ): Promise<{ statement: SettlementStatement }> {
    return this.request(`/api/admin/superadmin/settlement-statements/${statementId}/mark-paid`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async paySettlementStatementViaMercury(
    statementId: string,
    data?: { notes?: string }
  ): Promise<{ statement: SettlementStatement }> {
    return this.request(`/api/admin/superadmin/settlement-statements/${statementId}/pay-mercury`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }
}

export interface SettlementEntry {
  id: string;
  venue_id: string;
  payment_id: string;
  booking_id: string | null;
  entry_type: 'charge' | 'refund' | 'manual_adjustment';
  gross_cents: number;
  commission_bps: number;
  commission_cents: number;
  net_to_venue_cents: number;
  currency: string;
  settlement_status: 'pending' | 'settled' | 'voided';
  settled_at: string | null;
  settlement_reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SettlementStatement {
  id: string;
  venue_id: string;
  period_start: string;
  period_end: string;
  entry_count: number;
  gross_cents: number;
  commission_cents: number;
  net_to_venue_cents: number;
  status: 'draft' | 'finalized' | 'paid' | 'voided';
  payment_method: 'check' | 'mercury_ach' | string;
  payment_reference: string | null;
  finalized_at: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const api = new ApiClient();
